import { Button } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-hot-toast";
import ModalBasic from "../../../components/ModalBasic";
import { SettingsService } from "../services/settings.service";
import { UpdatePasswordDto } from "../types/settings.types";

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  submit?: string;
}

interface PasswordFormProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  isGoogleUser?: boolean;
  hasPassword?: boolean;
}

export default function PasswordForm({
  modalOpen,
  setModalOpen,
  isGoogleUser = false,
  hasPassword = true,
}: PasswordFormProps) {
  const [formData, setFormData] = useState<UpdatePasswordDto>({
    currentPassword: "",
    newPassword: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const { mutate: updatePassword, isPending } = useMutation({
    mutationFn: (data: UpdatePasswordDto) =>
      SettingsService.updatePassword(data),
    onSuccess: () => {
      toast.success("Password updated successfully");
      setFormData({ currentPassword: "", newPassword: "" });
      setModalOpen(false);
      setErrors({});
      setTouched({});
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to update password";
      setErrors({
        submit: errorMessage,
      });
      toast.error(errorMessage);
    },
  });

  const validateForm = (data: UpdatePasswordDto = formData) => {
    const newErrors: FormErrors = {};

    if (hasPassword) {
      if (!data.currentPassword) {
        newErrors.currentPassword = "Current password is required";
      } else if (data.currentPassword.length < 6) {
        newErrors.currentPassword = "Password must be at least 6 characters";
      }
    }

    if (!data.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (data.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    } else if (!/[A-Z]/.test(data.newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(data.newPassword)) {
      newErrors.newPassword = "Password must contain at least one number";
    } else if (data.newPassword === data.currentPassword) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      currentPassword: true,
      newPassword: true,
    });

    if (validateForm()) {
      updatePassword(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    setFormData(newData);

    if (touched[name]) {
      validateForm(newData);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateForm();
  };

  return (
    <ModalBasic
      id="password-modal"
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      title="Change Password"
    >
      <>
        {modalOpen && (
          <>
            {/* Modal content */}
            <div className="px-5 py-4">
              {isGoogleUser && !hasPassword && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 text-sm text-blue-600 dark:text-blue-400 rounded">
                  Since you signed up with Google, you need to set up a password
                  for additional security.
                </div>
              )}
              <div className="space-y-4">
                {hasPassword && (
                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      htmlFor="currentPassword"
                    >
                      Current Password <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="currentPassword"
                      name="currentPassword"
                      className={`form-input w-full px-2 py-1 ${
                        touched.currentPassword && errors.currentPassword
                          ? "border-rose-500"
                          : ""
                      }`}
                      type="password"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                    />
                    {touched.currentPassword && errors.currentPassword && (
                      <div className="text-xs mt-1 text-rose-500">
                        {errors.currentPassword}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="newPassword"
                  >
                    New Password <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    className={`form-input w-full px-2 py-1 ${
                      touched.newPassword && errors.newPassword
                        ? "border-rose-500"
                        : ""
                    }`}
                    type="password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                  {touched.newPassword && errors.newPassword && (
                    <div className="text-xs mt-1 text-rose-500">
                      {errors.newPassword}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Modal footer */}
            <div className={`px-5 py-4 border-t ${theme.border.primary}`}>
              <div className="flex flex-wrap justify-end space-x-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalOpen(false);
                  }}
                  variant="tertiary"
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={isPending}>
                  {isPending ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </div>
          </>
        )}
      </>
    </ModalBasic>
  );
}
